import {
  Device,
  Field,
  ForecastPageLimitsResponse,
  getLowerLimit,
  getUpperLimit,
  reduceForecastTemplateLimits,
  useActions,
  usePowerLevels,
  useStartOfWeek,
} from '@e2m-customer-portal/hooks';
import {
  InputFieldType,
  isMeasureWithValue,
  Measure,
  NullableMeasure,
  ValidationMode,
} from '@e2m-customer-portal/shared';
import { HourSelection, makeHourSelection, makeWeekdaySelection, WeekdaySelection } from '@e2m-customer-portal/ui';
import {
  addDays,
  addHours,
  eachDayOfInterval,
  eachHourOfInterval,
  endOfWeek,
  getTime,
  Interval,
  isBefore,
  startOfTomorrow,
  startOfWeek,
} from 'date-fns';
import { useState } from 'react';
import { DayOfWeek } from 'weekstart';
import { DefaultForecastLimitsQuery, Resolution, Weekday } from '../../../generated/graphql';
import { getEarliestMarketingDeadline, getMarketingDeadline } from '../../../utils';
import { useTemplatedForecastDataMutation } from './queries';

export function getSegmentedIntervals(
  interval: Interval,
  hours: HourSelection,
  weekdays: WeekdaySelection,
): Interval[] {
  const isSelected = (date: Date): boolean => hours[date.getHours()] && weekdays[date.getDay() as DayOfWeek];

  const intervals: Interval[] = [];
  let currentInterval: Interval | null = null;
  for (const date of eachHourOfInterval(interval)) {
    if (getTime(date) === getTime(interval.end)) {
      continue;
    }

    if (currentInterval) {
      if (isSelected(date)) {
        currentInterval.end = addHours(date, 1);
      } else {
        intervals.push(currentInterval);
        currentInterval = null;
      }
    } else if (isSelected(date)) {
      currentInterval = { start: date, end: addHours(date, 1) };
    }
  }
  if (currentInterval) {
    intervals.push(currentInterval);
  }

  return intervals;
}

function getWeekdays(weekdays: WeekdaySelection): Weekday[] {
  const mapping = {
    0: Weekday.SUNDAY,
    1: Weekday.MONDAY,
    2: Weekday.TUESDAY,
    3: Weekday.WEDNESDAY,
    4: Weekday.THURSDAY,
    5: Weekday.FRIDAY,
    6: Weekday.SATURDAY,
  };
  const result = [];
  for (let i = 0; i < 7; i++) {
    if (weekdays[i as DayOfWeek]) {
      result.push(mapping[i as DayOfWeek]);
    }
  }
  return result;
}

function getHours(hours: HourSelection): number[] {
  const result = [];
  for (let i = 0; i < 24; i++) {
    if (hours[i]) {
      result.push(i);
    }
  }
  return result;
}

export interface UpdateMutationResult {
  isLoading: boolean;
  update: () => Promise<void>;
}

export function useUpdateMutation(
  deviceId: string | undefined,
  interval: Interval,
  resolution: Resolution,
  hours: HourSelection,
  weekdays: WeekdaySelection,
  fields: readonly Field[],
  returnInterval?: Interval,
): UpdateMutationResult {
  const [doUpdate] = useTemplatedForecastDataMutation();
  const [isLoading, setIsLoading] = useState(false);

  return {
    isLoading,
    update: async () => {
      if (deviceId == null) {
        throw new TypeError('Expected device to be set.');
      }

      setIsLoading(true);
      try {
        await doUpdate(
          deviceId,
          interval,
          resolution,
          getWeekdays(weekdays),
          getHours(hours),
          fields
            .filter((field) => isMeasureWithValue(field.value))
            .map((field) => ({
              type: field.type,
              value: field.value as Measure,
            })),
          returnInterval,
        );
      } finally {
        setIsLoading(false);
      }
    },
  };
}

export function getMinDate(deadlines: undefined | Parameters<typeof getEarliestMarketingDeadline>[0]): Date {
  return deadlines?.length ? getMarketingDeadline(getEarliestMarketingDeadline(deadlines)) : startOfTomorrow();
}

function getInitialInterval(minimum: Date, weekStartsOn: DayOfWeek): Interval {
  const start = startOfWeek(addDays(minimum, 6), { weekStartsOn });
  return {
    start,
    end: endOfWeek(start, { weekStartsOn }),
  };
}

export function initializeFields(
  fields: readonly Field[],
  fieldLimits: ForecastPageLimitsResponse,
  lowerLimit: NullableMeasure,
  upperLimit: NullableMeasure,
  isControlEnergy: boolean,
  PUL?: number, // Power-Level Upper Limit
): readonly Field[] {
  return fieldLimits.fields.map((field) => {
    if (isControlEnergy) {
      return { type: field.type, value: { value: null, unit: 'kW' }, isComputed: true };
    }

    const previous = fields.find((pf) => pf.type === field.type);
    let value: NullableMeasure = previous?.value ?? { value: null, unit: 'kW' };
    let isComputed = false;

    if (!previous || previous.isComputed || previous.value.value == null) {
      let prefilledValue: NullableMeasure | undefined;
      let validatedLimit: NullableMeasure | undefined;
      let currentLimit: NullableMeasure | undefined;

      switch (field.type) {
        case InputFieldType.AFRR_UPPER_LIMIT:
        case InputFieldType.AFRR_POSITIVE:
        case InputFieldType.MFRR_UPPER_LIMIT:
        case InputFieldType.MFRR_POSITIVE:
        case InputFieldType.FLEX_PLUS_UPPER_LIMIT:
          prefilledValue = getUpperLimit(field.type, fields, fieldLimits, lowerLimit, upperLimit)?.limit;
          break;
        case InputFieldType.FCR:
          currentLimit = getUpperLimit(field.type, fields, fieldLimits, lowerLimit, upperLimit)?.limit;
          validatedLimit =
            PUL && currentLimit?.value && currentLimit.value >= currentLimit.value - PUL
              ? { ...currentLimit, value: Math.abs(currentLimit.value - PUL) }
              : currentLimit;
          prefilledValue = validatedLimit;
          break;
        case InputFieldType.AFRR_LOWER_LIMIT:
        case InputFieldType.AFRR_NEGATIVE:
        case InputFieldType.MFRR_LOWER_LIMIT:
        case InputFieldType.MFRR_NEGATIVE:
        case InputFieldType.FLEX_PLUS_LOWER_LIMIT:
          currentLimit = getLowerLimit(field.type, fieldLimits, lowerLimit)?.limit;
          validatedLimit =
            PUL && currentLimit?.value && currentLimit.value < PUL ? { ...currentLimit, value: PUL } : currentLimit;
          prefilledValue = validatedLimit;
          break;
      }

      if (prefilledValue) {
        value = prefilledValue;
        isComputed = true;
      }
    }

    return {
      type: field.type,
      value,
      isComputed,
    };
  });
}

export interface State {
  validationMode: ValidationMode;
  device: Device | null;
  interval: Interval;
  hours: HourSelection;
  weekdays: WeekdaySelection;
  disabledWeekdays: WeekdaySelection;
  fields: readonly Field[];
  error: Error | null;
}

export interface Actions {
  setOpen: (open: boolean) => void;
  setDevice: (device: Device | null) => void;
  setInterval: (option: { range: Interval }) => void;
  setHours: (hours: HourSelection) => void;
  setWeekdays: (weekdays: WeekdaySelection) => void;
  setFields: (fields: readonly Field[]) => void;
  setLimits: (template: DefaultForecastLimitsQuery['getDefaultForecast'], fields: ForecastPageLimitsResponse) => void;
  showValidationErrors: () => void;
  setError: (error: Error) => void;
  clearError: () => void;
}

export function useAssistantState(
  open: boolean,
  device: Device,
  isControlEnergy: boolean,
  powerLevel?: number, // Only used for testing to avoid the power levels query
): [State, Actions] {
  const weekStartsOn = useStartOfWeek();

  const powerLevels = usePowerLevels(open ? device.id : undefined);
  // When one power level is present we extract the value to validate the prefilled limits in the useAssistantState hook
  const PUL = powerLevel ?? (powerLevels && powerLevels.length === 1 ? Number(powerLevels[0].value) : undefined);

  const initialDevice = device;
  return useActions<
    State & {
      limits: {
        fields: ForecastPageLimitsResponse;
        template: DefaultForecastLimitsQuery['getDefaultForecast'];
      } | null;
    },
    Actions
  >(
    {
      validationMode: 'off' as ValidationMode,
      device: initialDevice,
      interval: getInitialInterval(getMinDate(initialDevice?.deadlines), weekStartsOn),
      hours: makeHourSelection(false, isControlEnergy ? 4 : 1),
      weekdays: makeWeekdaySelection(false),
      disabledWeekdays: makeWeekdaySelection(false),
      fields: [],
      error: null,
      limits: null,
    },
    {
      setOpen(o) {
        if (o && this.validationMode === 'off') {
          // initialize state when opening the drawer
          const nextSelectedDevice = device;
          const nextMinDate = getMinDate(nextSelectedDevice.deadlines);

          const state = {
            ...this,
            validationMode: 'blur' as ValidationMode,
            device: nextSelectedDevice,
            interval: getInitialInterval(nextMinDate, weekStartsOn),
            hours: makeHourSelection(false, isControlEnergy ? 4 : 1),
            weekdays: makeWeekdaySelection(false),
            disabledWeekdays: makeWeekdaySelection(false),
            fields: [] as readonly Field[],
            error: null,
          };

          if (!open) return state;

          if (this.limits) {
            const { minimum, maximum } = reduceForecastTemplateLimits(this.limits.template, state.interval);
            state.fields = initializeFields(state.fields, this.limits.fields, minimum, maximum, isControlEnergy, PUL);
          }

          return state;
        }

        if (!o && this.validationMode !== 'off') {
          // reset validation errors when closing the drawer
          return {
            ...this,
            validationMode: 'off',
          };
        }

        return this;
      },

      setDevice(device) {
        const state = {
          ...this,
          validationMode: 'blur' as ValidationMode,
          device,
          fields: [] as readonly Field[],
        };

        const nextMinDate = getMinDate(device?.deadlines);
        if (isBefore(state.interval.start, nextMinDate)) {
          state.interval = getInitialInterval(nextMinDate, weekStartsOn);
          state.disabledWeekdays = makeWeekdaySelection(false);
        }

        if (!open) return state;

        if (this.limits) {
          const { minimum, maximum } = reduceForecastTemplateLimits(this.limits.template, state.interval);
          state.fields = initializeFields(state.fields, this.limits.fields, minimum, maximum, isControlEnergy, PUL);
        }

        return state;
      },

      setInterval(o) {
        const state = {
          ...this,
          interval: o.range,
          weekdays: {} as WeekdaySelection,
          disabledWeekdays: {} as WeekdaySelection,
        };

        const days = eachDayOfInterval(o.range).filter((d) => getTime(d) !== getTime(o.range.end));
        for (let day = 0; day < 7; day++) {
          const inInterval = days.some((d) => d.getDay() === day);
          state.weekdays[day as DayOfWeek] = days.length === 1 && inInterval;
          state.disabledWeekdays[day as DayOfWeek] = !inInterval;
        }

        if (!open) return state;

        if (this.limits) {
          const { minimum, maximum } = reduceForecastTemplateLimits(this.limits.template, o.range);
          state.fields = initializeFields(state.fields, this.limits.fields, minimum, maximum, isControlEnergy, PUL);
        }

        return state;
      },

      setHours(hours) {
        return {
          ...this,
          hours,
        };
      },

      setWeekdays(weekdays) {
        return {
          ...this,
          weekdays,
        };
      },

      setFields(fields) {
        return {
          ...this,
          fields,
        };
      },

      setLimits(template, f) {
        if (!open) return this;

        const { minimum, maximum } = reduceForecastTemplateLimits(template, this.interval);

        return {
          ...this,
          limits: {
            fields: f,
            template,
          },
          fields: initializeFields(this.fields, f, minimum, maximum, isControlEnergy, PUL),
        };
      },

      showValidationErrors() {
        return {
          ...this,
          validationMode: 'on',
        };
      },

      setError(error) {
        return {
          ...this,
          error,
        };
      },

      clearError() {
        return {
          ...this,
          error: null,
        };
      },
    },
  );
}
